import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import NoticiaImage from './NoticiaImage';

import {

  parseNoticiaImagenes,

  getYoutubeEmbedUrl,

  getYoutubeThumbnailUrl,

  buildNoticiaCardSlides,

} from '../utils/noticiaMedia';

import { FaChevronLeft, FaChevronRight, FaExpand, FaPlay, FaTimes } from 'react-icons/fa';

import './NoticiaCardMedia.css';



const buildEmbedSrc = (embedUrl, extra = '') => {
  if (!embedUrl) return '';
  const sep = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${sep}rel=0${extra ? `&${extra}` : ''}`;
};



export const NoticiaVideoLightbox = ({ embedUrl, onClose }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="noticia-video-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Video en pantalla completa"
      onClick={onClose}
    >
      <div className="noticia-video-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="noticia-video-lightbox-close"
          onClick={onClose}
          aria-label="Cerrar video"
        >
          <FaTimes aria-hidden />
        </button>
        <div className="noticia-video-lightbox-player">
          <iframe
            src={buildEmbedSrc(embedUrl, 'autoplay=1')}
            title="Video de la noticia en pantalla completa"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    </div>,
    document.body
  );
};



export const NoticiaVideoBlock = ({ embedUrl }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className="noticia-card-media-video">
        <iframe
          src={buildEmbedSrc(embedUrl)}
          title="Video de la noticia"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
        <button
          type="button"
          className="noticia-card-media-fs"
          onClick={() => setExpanded(true)}
          aria-label="Ver video en pantalla completa"
        >
          <FaExpand aria-hidden />
          <span>Pantalla completa</span>
        </button>
      </div>
      {expanded && (
        <NoticiaVideoLightbox embedUrl={embedUrl} onClose={() => setExpanded(false)} />
      )}
    </>
  );
};



export const NoticiaVideoThumbnail = ({ videoUrl, embedUrl, title, onPlay }) => {

  const [thumbQuality, setThumbQuality] = useState('hqdefault');

  const thumbSrc = getYoutubeThumbnailUrl(videoUrl || embedUrl, thumbQuality);



  return (

    <button

      type="button"

      className="noticia-video-thumbnail"

      onClick={(e) => {

        e.stopPropagation();

        onPlay();

      }}

      aria-label={title ? `Reproducir video: ${title}` : 'Reproducir video'}

    >

      {thumbSrc ? (

        <img

          src={thumbSrc}

          alt=""

          className="noticia-video-thumbnail-img"

          onError={() => {

            if (thumbQuality !== 'mqdefault') setThumbQuality('mqdefault');

          }}

        />

      ) : (

        <span className="noticia-video-thumbnail-fallback" aria-hidden />

      )}

      <span className="noticia-video-thumbnail-overlay" aria-hidden>

        <span className="noticia-video-thumbnail-play">

          <FaPlay />

        </span>

      </span>

    </button>

  );

};



const NoticiaCardSingleVideo = ({ slide, alt }) => {

  const [playing, setPlaying] = useState(false);

  if (playing) {

    return <NoticiaVideoBlock embedUrl={slide.embedUrl} />;

  }

  return (

    <NoticiaVideoThumbnail

      videoUrl={slide.videoUrl}

      embedUrl={slide.embedUrl}

      title={alt}

      onPlay={() => setPlaying(true)}

    />

  );

};



const CarouselControls = ({ idx, n, onPrev, onNext, onGoTo, slides }) => (

  <>

    <button

      type="button"

      className="noticia-card-media-nav prev"

      onClick={(e) => {

        e.stopPropagation();

        onPrev();

      }}

      aria-label="Anterior"

    >

      <FaChevronLeft />

    </button>

    <button

      type="button"

      className="noticia-card-media-nav next"

      onClick={(e) => {

        e.stopPropagation();

        onNext();

      }}

      aria-label="Siguiente"

    >

      <FaChevronRight />

    </button>

    <div className="noticia-card-media-dots">

      {Array.from({ length: n }).map((_, i) => (

        <button

          key={i}

          type="button"

          className={`noticia-card-media-dot ${i === idx ? 'active' : ''} ${slides?.[i]?.type === 'video' ? 'is-video' : ''}`}

          onClick={(e) => {

            e.stopPropagation();

            onGoTo(i);

          }}

          aria-label={slides?.[i]?.type === 'video' ? 'Ir al video' : `Ir a imagen ${i + 1}`}

          aria-current={i === idx}

        />

      ))}

    </div>

    <span className="noticia-card-media-counter" aria-live="polite">

      {idx + 1} / {n}

    </span>

  </>

);



export const NoticiaImagenesCarousel = ({ urls, alt, wrapperClassName }) => {

  const [idx, setIdx] = useState(0);

  const n = urls.length;

  const urlsKey = urls.join('|');



  useEffect(() => {

    setIdx(0);

  }, [urlsKey]);



  if (n <= 1) return null;



  const goTo = (next) => setIdx((next + n) % n);



  return (

    <div className="noticia-card-media-carousel">

      <div

        className="noticia-card-media-track"

        style={{ transform: `translateX(-${idx * 100}%)` }}

      >

        {urls.map((src, i) => (

          <div key={`${src}-${i}`} className="noticia-card-media-slide">

            <NoticiaImage src={src} alt={`${alt} — ${i + 1}`} className={wrapperClassName || 'noticia-image-wrapper-inner'} />

          </div>

        ))}

      </div>

      <CarouselControls

        idx={idx}

        n={n}

        onPrev={() => goTo(idx - 1)}

        onNext={() => goTo(idx + 1)}

        onGoTo={goTo}

      />

    </div>

  );

};



export const NoticiaCardMediaCarousel = ({ slides, alt }) => {

  const [idx, setIdx] = useState(0);

  const [videoPlaying, setVideoPlaying] = useState(false);

  const n = slides.length;

  const slidesKey = slides.map((s) => (s.type === 'image' ? s.src : s.embedUrl)).join('|');



  useEffect(() => {

    setIdx(0);

    setVideoPlaying(false);

  }, [slidesKey]);



  useEffect(() => {

    setVideoPlaying(false);

  }, [idx]);



  if (n <= 1) return null;



  const goTo = (next) => setIdx((next + n) % n);



  return (

    <div className="noticia-card-media-carousel">

      <div

        className="noticia-card-media-track"

        style={{ transform: `translateX(-${idx * 100}%)` }}

      >

        {slides.map((slide, i) => (

          <div key={`${slide.type}-${i}`} className="noticia-card-media-slide">

            {slide.type === 'video' ? (

              videoPlaying && i === idx ? (

                <NoticiaVideoBlock embedUrl={slide.embedUrl} />

              ) : (

                <NoticiaVideoThumbnail

                  videoUrl={slide.videoUrl}

                  embedUrl={slide.embedUrl}

                  title={alt}

                  onPlay={() => {

                    setIdx(i);

                    setVideoPlaying(true);

                  }}

                />

              )

            ) : (

              <NoticiaImage src={slide.src} alt={`${alt} — ${i + 1}`} className="noticia-image-wrapper-inner" />

            )}

          </div>

        ))}

      </div>

      <CarouselControls

        idx={idx}

        n={n}

        slides={slides}

        onPrev={() => goTo(idx - 1)}

        onNext={() => goTo(idx + 1)}

        onGoTo={goTo}

      />

    </div>

  );

};



const NoticiaCardMedia = ({

  noticia,

  fallbackImageSrc,

  wrapperClassName = 'noticia-card-image',

  variant = 'card',

}) => {

  const urls = parseNoticiaImagenes(noticia);

  const embedUrl = getYoutubeEmbedUrl(noticia?.video_url);

  const alt = noticia?.titulo || 'Noticia';

  const isCard = variant === 'card';

  const cardSlides = isCard ? buildNoticiaCardSlides(noticia) : [];



  if (isCard && cardSlides.length > 1) {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant} data-has-carousel="true">

        <NoticiaCardMediaCarousel slides={cardSlides} alt={alt} />

      </div>

    );

  }



  if (isCard && cardSlides.length === 1 && cardSlides[0].type === 'video') {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant}>

        <NoticiaCardSingleVideo slide={cardSlides[0]} alt={alt} />

      </div>

    );

  }



  if (urls.length > 1) {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant} data-has-carousel="true">

        <NoticiaImagenesCarousel urls={urls} alt={alt} wrapperClassName="noticia-image-wrapper-inner" />

      </div>

    );

  }



  if (urls.length === 1) {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant}>

        <NoticiaImage src={urls[0]} alt={alt} className="noticia-image-wrapper-inner" />

      </div>

    );

  }



  if (embedUrl) {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant}>

        <NoticiaVideoBlock embedUrl={embedUrl} />

      </div>

    );

  }



  if (fallbackImageSrc) {

    return (

      <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant}>

        <NoticiaImage src={fallbackImageSrc} alt={alt} className="noticia-image-wrapper-inner" />

      </div>

    );

  }



  return (

    <div className={`noticia-card-media ${wrapperClassName}`} data-variant={variant}>

      <NoticiaImage src={null} alt={alt} className="noticia-image-wrapper-inner" />

    </div>

  );

};



export default NoticiaCardMedia;

